import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { 
  gethistoryData, 
  downloadProcessedData, 
  downloadFile,
  // New APIs for Data Matching - you'll need to implement these
  downloadInputFile,
  downloadModelFile,
  downloadAllFiles,
  downloadClassificationOutputFile, // ← NEW, Classification manual-only S3 download
  downloadProcessedDataViaS3, // ← NEW, Data Matching manual output S3 download
} from "../model/DataProcessingModel.jsx";

const DataHistoryVM = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleToastOpen } = useToast();
  
  const [pageTitle, setPageTitle] = useState("Processing History");
  const [role, setRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [selectedTool, setSelectedTool] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [dialogDownloadLoading, setDialogDownloadLoading] = useState(false);
  const [downloadBatchInfo, setDownloadBatchInfo] = useState({
    batchId: "",
    toolName: "",
    modelName: "",
    project: ""
  });

  // New state for Data Matching download modal
  const [dataMatchingDownloadModalOpen, setDataMatchingDownloadModalOpen] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState("");
  const [dataMatchingDownloadInfo, setDataMatchingDownloadInfo] = useState({
    batchId: "",
    toolName: "",
    modelName: "",
    dateTime: ""
  });

  const pollingIntervalRef = useRef(null);
  const POLLING_INTERVAL = 30000;

  // Determine if we should show the enrichment table
  const isEnrichmentHistory = pageTitle === "Enrichment History";
  const isDataMatchingTool = selectedTool === "Data Matching";

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const toolParam = queryParams.get('tool');
    const decodeData = toolParam ? decodeURIComponent(toolParam) : null;
    
    if (decodeData) {
      setSelectedTool(decodeData);
      setPageTitle(`${decodeData} History`);
    } else {
      setPageTitle("Processing History");
    }
  }, [location.search]);

  const fetchData = async () => {
    try {
      const response = await gethistoryData(selectedTool);
      if (response && response.data) {
        setRole(response.role);
        setHistoryData(response.data);
        const filtered = response.data.filter((record) => {
          const modelName = record.model_name || "";
          return modelName.toLowerCase().includes(searchTerm.toLowerCase());
        });
        setFilteredRecords(filtered);
      } else {
        setHistoryData([]);
        setFilteredRecords([]);
      }
    } catch (error) {
      setHistoryData([]);
      setFilteredRecords([]);
      handleToastOpen("error", "Failed to fetch history data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTool) {
      setIsLoading(true);
      fetchData();
    }
  }, [selectedTool]);

  useEffect(() => {
    if (selectedTool) {
      pollingIntervalRef.current = setInterval(() => {
        fetchData();
      }, POLLING_INTERVAL);

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }
  }, [selectedTool]);

  useEffect(() => {
    if (historyData && historyData.length > 0) {
      const term = searchTerm.trim();
      if (!term) {
        setFilteredRecords(historyData);
      } else {
        const cleanSearchTerm = term.toLowerCase().replace(/[^\w\s]/g, '');
        const isNumericSearch = /^\d+$/.test(term);
        
        const filtered = historyData.filter((record) => {
          if (isNumericSearch) {
            for (const key in record) {
              if (record[key] !== null && record[key] !== undefined) {
                const recordValue = String(record[key]);
                if (recordValue === term) {
                  return true;
                }
              }
            }
          }

          if (record.tool_name && record.model_name) {
            const displayFileName = `${record.tool_name.replace(/[\s-]+/g, '_')}_${record.model_name.replace(/[\s-]+/g, '_')}`;
            if (displayFileName.toLowerCase().includes(term.toLowerCase())) {
              return true;
            }
            const searchWithSpaces = term.toLowerCase().replace(/_+/g, ' ');
            if (record.tool_name.toLowerCase().includes(searchWithSpaces) || 
                record.model_name.toLowerCase().includes(searchWithSpaces)) {
              return true;
            }
          }

          for (const key in record) {
            if (record[key] !== null && record[key] !== undefined) {
              const originalValue = String(record[key]);
              const cleanValue = originalValue.toLowerCase().replace(/[^\w\s]/g, '');
              
              if (originalValue.toLowerCase().includes(term.toLowerCase())) {
                return true;
              }
              if (cleanValue.includes(cleanSearchTerm)) {
                return true;
              }

              const searchWords = cleanSearchTerm.split(/\s+/).filter(w => w.length > 0);
              if (searchWords.length > 0) {
                const allWordsMatch = searchWords.every(word => cleanValue.includes(word));
                if (allWordsMatch) {
                  return true;
                }
              }
            }
          }
          return false;
        });
        setFilteredRecords(filtered);
      }
      setPage(0);
    } else {
      setFilteredRecords([]);
    }
  }, [searchTerm, historyData]);

  // Event Handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleGoHome = () => {
    if (selectedTool) {
      localStorage.setItem('selectedDataTool', selectedTool);
    }
    navigate("/data-operation-centre");
  };

  // Enhanced download function for regular tools (existing behavior)
  const handleDownload = async (batchId, toolname, modelname, datetime) => {
    // For Data Matching tool, open the file type selection modal
    if (isDataMatchingTool) {
      setDataMatchingDownloadInfo({
        batchId,
        toolName: toolname,
        modelName: modelname,
        dateTime: datetime
      });
      setDataMatchingDownloadModalOpen(true);
      return;
    }

    // ═══ Classification MANUAL only: S3 presigned-URL flow (History tab) ═══
    // E2E Classification records have model_name === "E2E" — those keep
    // using the base64 /output_file flow below, unchanged.
    if (selectedTool === "Classification" && modelname !== "E2E") {
      setIsDownloading(true);
      try {
        const result = await downloadClassificationOutputFile(selectedTool, batchId);

        if (!result?.download_url) {
          throw new Error("Server did not return a download URL");
        }

        const link = document.createElement("a");
        link.href = result.download_url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        handleToastOpen("success", "Excel File Downloaded Successfully");
      } catch (error) {
        console.error("=== CLASSIFICATION DOWNLOAD ERROR ===");
        console.error("Error details:", {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        if (error.name !== 'AbortError') {
          handleToastOpen("error", "Excel File Download Failed: " + (error.message || "Unknown error"));
        }
      } finally {
        setIsDownloading(false);
      }
      return;
    }

    // For all other tools, use existing download logic
    setIsDownloading(true);
    try {
      const formattedDateTime = datetime.replace(/\s+/g, '_').replace(/:/g, '');
      const fileName = `${toolname}_${modelname}_${formattedDateTime}`;
      const response = await downloadProcessedData(selectedTool, batchId, fileName);
      
      if (!response || !response.base64Data) {
        throw new Error("No base64 data received from API");
      }

      const { base64Data } = response;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `${fileName}.xlsx`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      handleToastOpen("success", "Excel File Downloaded Successfully");
    } catch (error) {
      console.error("=== DOWNLOAD ERROR ===");
      console.error("Error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      if (error.name !== 'AbortError') {
        handleToastOpen("error", "Excel File Download Failed: " + (error.message || "Unknown error"));
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // New function to handle Data Matching file downloads
  const handleDataMatchingDownload = async () => {
    if (!selectedFileType) {
      handleToastOpen("error", "Please select a file type to download");
      return;
    }

    setIsDownloading(true);
    try {
      const { batchId, toolName, modelName, dateTime } = dataMatchingDownloadInfo;
      const formattedDateTime = dateTime.replace(/\s+/g, '_').replace(/:/g, '');
      let response;
      let fileName;

      switch (selectedFileType) {
        case "output": {
          // ═══ Output file: S3 presigned-URL flow (/output_file_s3) ═══
          const outputResult = await downloadProcessedDataViaS3(selectedTool, batchId, modelName);

          if (!outputResult?.download_url) {
            throw new Error("Server did not return a download URL");
          }

          const outputLink = document.createElement("a");
          outputLink.href = outputResult.download_url;
          document.body.appendChild(outputLink);
          outputLink.click();
          document.body.removeChild(outputLink);

          handleToastOpen("success", "Output file downloaded successfully");
          setDataMatchingDownloadModalOpen(false);
          setSelectedFileType("");
          setIsDownloading(false);
          return;
        }
        case "input":
          // New API for input file
          fileName = `${toolName}_${modelName}_${formattedDateTime}_input`;
          response = await downloadInputFile(selectedTool, batchId, fileName);
          break;
        case "model":
          // New API for model file
          fileName = `${toolName}_${modelName}_${formattedDateTime}_model`;
          response = await downloadModelFile(selectedTool, batchId, fileName);
          break;
        case "all":
          // New API for all files (might return a zip)
          fileName = `${toolName}_${modelName}_${formattedDateTime}_all`;
          response = await downloadAllFiles(selectedTool, batchId, fileName);
          break;
        default:
          throw new Error("Invalid file type selected");
      }

      if (!response || !response.base64Data) {
        throw new Error("No base64 data received from API");
      }

      const { base64Data } = response;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      // Determine file type based on selection
      const fileExtension = selectedFileType === "all" ? "zip" : "xlsx";
      const mimeType = selectedFileType === "all" 
        ? "application/zip" 
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const blob = new Blob([byteArray], { type: mimeType });

      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `${fileName}.${fileExtension}`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      handleToastOpen("success", `${selectedFileType.charAt(0).toUpperCase() + selectedFileType.slice(1)} file downloaded successfully`);
      setDataMatchingDownloadModalOpen(false);
      setSelectedFileType("");
    } catch (error) {
      console.error("=== DATA MATCHING DOWNLOAD ERROR ===");
      console.error("Error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      handleToastOpen("error", `Download Failed: ${error.message || "Unknown error"}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCloseBackdrop = () => {
    setIsDownloading(false);
  };

  const handleProcessClick = (batchId, toolName, modelName, totalRecords, uniqueRecords) => {
    const queryParams = new URLSearchParams({
      batchId,
      toolName,
      modelName,
      totalRecords,
      uniqueRecords,
      toBeProcessed: true
    }).toString();
    navigate(`/data-operation-centre?${queryParams}`);
  };

  const handleBackToHistory = () => {
    setShowDetailView(false);
    setSelectedBatch(null);
    setSelectedProject(null);
    setSelectedCustomer(null);
  };

  const handleEnrichmentProcessClick = async (batchId, customerName, projectName) => {
    setSelectedBatch(batchId);
    setSelectedCustomer(customerName);
    setSelectedProject(projectName);
    setShowDetailView(true);
    navigate(`/data-operation-centre/detail-history?batchId=${batchId}&customer=${encodeURIComponent(customerName)}&project=${encodeURIComponent(projectName)}`);
  };

  const handleEnrichmentDownload = (batchId, toolName, modelName, project) => {
    setDownloadBatchInfo({ batchId, toolName, modelName, project });
    setDownloadDialogOpen(true);
  };

  const handleDownloadDialogClose = () => {
    setDownloadDialogOpen(false);
    setSelectedStatus("");
  };

  const handleConfirmDownload = async () => {
    setDialogDownloadLoading(true);
    try {
      const { batchId, toolName, modelName, project } = downloadBatchInfo;
      
      if (!selectedStatus || selectedStatus.trim() === "") {
        handleToastOpen("error", "Please select a status first");
        return;
      }

      const downloadData = await downloadFile(selectedStatus, batchId);
      
      if (!downloadData?.base64Data) {
        throw new Error("Invalid file data received");
      }

      const formatName = (name) => {
        return name ? name.replace(/[\s-]+/g, '_') : '';
      };

      const formattedTool = formatName(toolName);
      const formattedProject = formatName(project);
      const formattedModel = formatName(modelName);
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
      const formattedDateTime = `${dateStr}_${timeStr}`;

      const filename = `${formattedTool}_${formattedModel}_${formattedProject}_${formattedDateTime}.xlsx`;

      const binaryString = atob(downloadData.base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(downloadUrl);
      }, 100);

      handleToastOpen("success", "Excel File Downloaded Successfully");
      setDownloadDialogOpen(false);
    } catch (error) {
      setDownloadDialogOpen(false);
      handleToastOpen("error", "No records found!");
      console.error("Download Error:", error);
    } finally {
      setDialogDownloadLoading(false);
    }
  };

  // Data Matching download modal handlers
  const handleDataMatchingDownloadModalClose = () => {
    setDataMatchingDownloadModalOpen(false);
    setSelectedFileType("");
    setDataMatchingDownloadInfo({
      batchId: "",
      toolName: "",
      modelName: "",
      dateTime: ""
    });
  };

  // Utility Functions
  const formatAccuracy = (value) => {
    if (value === 0 || value === null || value === undefined) {
      return "0%";
    }
    return `${value}%`;
  };

  const getAccuracyColor = (percentage) => {
    if (percentage === null || percentage === undefined) return "default";
    if (percentage >= 76) return "success";
    if (percentage >= 51) return "warning";
    return "error";
  };

  // Replace the entire getStatusCellConfig function in your code with this:
  const getStatusCellConfig = (record, isEnrichment = false) => {
    const lowerStatus = record.status?.toLowerCase();

    // Standard button styling configuration with same height and width as start enrichment
    const standardButtonStyles = {
      borderRadius: '12px',
      fontSize: '0.625rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      padding: '2px 6px !important',
      minWidth: '120px !important',
      width: '120px !important',
      height: '24px !important',
      maxWidth: '120px',
      lineHeight: '1',
      minHeight: '24px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&.MuiButton-root': {
        padding: '2px 6px !important',
        minWidth: '120px !important',
        width: '120px !important',
        height: '24px !important'
      },
      '& .MuiButton-root': {
        padding: '2px 6px !important',
        minWidth: '120px !important',
        height: '24px !important'
      }
    };

    // Button states for actionable statuses
    if (lowerStatus === "to be processed" || 
        lowerStatus === "start processing" || 
        lowerStatus === "start enrichment" || 
        lowerStatus === "in progress" || 
        lowerStatus === "processing" || 
        lowerStatus === "processed") {
      
      // Determine button color based on status (keeping original colors)
      let buttonColor, backgroundColor, borderColor, hoverColor;

      if (lowerStatus === "in progress" || lowerStatus === "processing") {
        // Yellow theme for in progress/processing
        buttonColor = record.edit ? '#daa520' : '#9e9e9e';
        backgroundColor = record.edit ? 'rgba(255, 223, 0, 0.1)' : 'rgba(204, 204, 220, 0.3)';
        borderColor = record.edit ? '#eec900' : '#d3d3d3';
        hoverColor = 'rgba(255, 223, 0, 0.2)';
      } else if (lowerStatus === "processed") {
        // Green theme for processed
        buttonColor = record.edit ? '#2e7d32' : '#9e9e9e';
        backgroundColor = record.edit ? 'rgba(76, 175, 80, 0.1)' : 'rgba(204, 204, 220, 0.3)';
        borderColor = record.edit ? '#4caf50' : '#d3d3d3';
        hoverColor = 'rgba(76, 175, 80, 0.2)';
      } else {
        // Blue theme for other actionable statuses
        buttonColor = record.edit ? 'rgb(0, 143, 238)' : '#9e9e9e';
        backgroundColor = record.edit ? 'rgba(0, 38, 255, 0.1)' : 'rgba(204, 204, 220, 0.3)';
        borderColor = record.edit ? 'rgb(0, 143, 238)' : '#d3d3d3';
        hoverColor = 'rgba(0, 162, 255, 0.2)';
      }

      return {
        type: "button",
        config: {
          onClick: record.edit ? (isEnrichment 
            ? () => handleEnrichmentProcessClick(record.batch_id, record.model_name, record.project)
            : () => handleProcessClick(record.batch_id, record.tool_name, record.model_name, record.total_records, record.unique_records)
          ) : undefined,
          disabled: !record.edit, // Consistent disabled logic for all statuses
          label: lowerStatus === "start processing" ? "Start Processing" :
                 lowerStatus === "start enrichment" ? "Start Enrichment" :
                 lowerStatus === "in progress" ? "In Progress" :
                 lowerStatus === "processing" ? "Processing" :
                 lowerStatus === "processed" ? "Processed" : "To Be Processed",
          sx: {
            ...standardButtonStyles,
            backgroundColor: backgroundColor,
            color: buttonColor,
            border: `1px solid ${borderColor}`,
            cursor: record.edit ? 'pointer' : 'not-allowed',
            pointerEvents: record.edit ? 'auto' : 'none',
            '&:hover': record.edit ? {
              backgroundColor: hoverColor,
            } : {},
            '&:disabled': {
              backgroundColor: 'rgba(204, 204, 220, 0.3)',
              color: '#9e9e9e',
              border: '1px solid #d3d3d3',
              cursor: 'not-allowed'
            }
          }
        }
      };
    }
    // For completed status - make it a button but NOT clickable
    else if (lowerStatus === "completed") {
      return {
        type: "button",
        config: {
          onClick: undefined, // Remove onClick handler completely
          disabled: !record.edit, // Disabled only if edit is false
          label: "Completed",
          sx: {
            ...standardButtonStyles,
            backgroundColor: record.edit ? 'rgba(76, 175, 80, 0.1)' : 'rgba(204, 204, 220, 0.3)',
            color: record.edit ? '#2e7d32' : '#9e9e9e',
            border: record.edit ? '1px solid #4caf50' : '1px solid #d3d3d3',
            cursor: 'not-allowed', // Always not-allowed since no onClick
            pointerEvents: 'none', // Always disable pointer events since no onClick
            '&:disabled': {
              backgroundColor: 'rgba(204, 204, 220, 0.3)',
              color: '#9e9e9e',
              border: '1px solid #d3d3d3',
              cursor: 'not-allowed'
            }
          }
        }
      };
    }
    // For failed status - make it a button with same dimensions as completed
    else if (lowerStatus === "failed") {
      return {
        type: "button",
        config: {
          onClick: undefined, // No click handler for failed status
          disabled: !record.edit, // Disabled only if edit is false
          label: "Failed",
          sx: {
            ...standardButtonStyles,
            backgroundColor: record.edit ? 'rgba(244, 67, 54, 0.1)' : 'rgba(204, 204, 220, 0.3)',
            color: record.edit ? '#d32f2f' : '#9e9e9e',
            border: record.edit ? '1px solid #f44336' : '1px solid #d3d3d3',
            cursor: 'not-allowed', // Always not-allowed since no onClick
            pointerEvents: 'none', // Always disable pointer events since no onClick
            '&:disabled': {
              backgroundColor: 'rgba(204, 204, 220, 0.3)',
              color: '#9e9e9e',
              border: '1px solid #d3d3d3',
              cursor: 'not-allowed'
            }
          }
        }
      };
    }
    // Static status boxes for ALL other non-actionable statuses
    else {
      return {
        type: "box",
        config: {
          content: record.status || "Unknown",
          sx: {
            ...standardButtonStyles,
            cursor: 'default',
            backgroundColor: record.edit ? (
              lowerStatus === "processing" || lowerStatus === "in progress" 
                ? 'rgba(255, 223, 0, 0.1)' 
                : 'rgba(158, 158, 158, 0.1)'
            ) : 'rgba(204, 204, 220, 0.3)',
            color: record.edit ? (
              lowerStatus === "processing" || lowerStatus === "in progress" 
                ? '#daa520' 
                : '#757575'
            ) : '#9e9e9e',
            border: record.edit 
              ? `1px solid ${lowerStatus === "processing" || lowerStatus === "in progress" ? '#eec900' : '#9e9e9e'}`
              : '1px solid #d3d3d3'
          }
        }
      };
    }
  };

  // Column Definitions
  const getBaseColumns = () => {
    const columns = [
      {
        field: "file_name",
        header: "File Name",
        minWidth: "150px",
        render: (record) => ({
          type: "text",
          content: (record.tool_name && record.model_name) 
            ? `${record.tool_name.replace(/[\s-]+/g, '_')}_${record.model_name.replace(/[\s-]+/g, '_')}` 
            : "Unknown"
        })
      },
      {
        field: "date_time",
        header: "Date",
        align: "center",
        minWidth: "80px", // Reduced from 120px
        render: (row) => {
          const date = new Date(row.date_time);
          return date.toLocaleDateString('en-CA'); // YYYY-MM-DD format
        }
      },
      {
        field: "unique_records",
        header: "Unique",
        align: "center",
        minWidth: "110px",
        render: (record) => ({
          type: "text",
          content: record.unique_records?.toLocaleString() || "0"
        })
      },
      {
        field: "processed_records",
        header: "Processed",
        align: "center",
        minWidth: "110px",
        render: (record) => ({
          type: "text",
          content: record.processed_records?.toLocaleString() || "0"
        })
      },
      {
        field: "total_records",
        header: "Total",
        align: "center",
        minWidth: "100px",
        render: (record) => ({
          type: "text",
          content: record.total_records?.toLocaleString() || "0"
        })
      },
      {
        field: "accuracy_percentage",
        header: "Accuracy",
        align: "center",
        minWidth: "90px",
        render: (record) => ({
          type: "box",
          config: {
            content: formatAccuracy(record.accuracy_percentage),
            sx: {
              height: '24px',
              minHeight: '24px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              fontSize: '0.625rem',
              fontWeight: 500,
              padding: '2px 6px',
              minWidth: '60px',
              border: `1px solid ${getAccuracyColor(record.accuracy_percentage) === 'success' ? '#4caf50' : 
                      getAccuracyColor(record.accuracy_percentage) === 'warning' ? '#ff9800' : '#f44336'}`,
              backgroundColor: `${getAccuracyColor(record.accuracy_percentage) === 'success' ? 'rgba(76, 175, 80, 0.1)' : 
                               getAccuracyColor(record.accuracy_percentage) === 'warning' ? 'rgba(255, 152, 0, 0.1)' : 'rgba(244, 67, 54, 0.1)'}`,
              color: `${getAccuracyColor(record.accuracy_percentage) === 'success' ? '#2e7d32' : 
                     getAccuracyColor(record.accuracy_percentage) === 'warning' ? '#e65100' : '#d32f2f'}`
            }
          }
        })
      },
      {
        field: "status",
        header: "Status",
        align: "center",
        minWidth: "90px",
        render: (record) => getStatusCellConfig(record, false)
      }
    ];

    // Add created_by column if not MDM member
    if (role !== "MDM Members") {
      columns.push({
        field: "created_by",
        header: "Created By",
        align: "center",
        minWidth: "90px",
        render: (record) => ({
          type: "text",
          content: record.created_by || "0"
        })
      });
    }

    // Fixed actions column with better event handling
    columns.push({
      field: "actions",
      header: "Actions",
      align: "center",
      minWidth: "70px",
      render: (record) => {
        const statusLower = record.status?.toLowerCase();
        const isCompleted = statusLower === "completed";
        const isProcessed = statusLower === "processed";
        const canDownload = isCompleted || isProcessed;

        return {
          type: "iconButton",
          config: {
            icon: <FileDownloadIcon style={{ color: canDownload ? "#002A5B" : "#cccccc" }} />,
            // Fixed: Ensure onClick is always defined when canDownload is true
            onClick: canDownload ? () => {
              handleDownload(record.batch_id, record.tool_name, record.model_name, record.date_time);
            } : () => { },
            disabled: !canDownload,
            tooltip: canDownload ? "Download" : `Cannot download (Status: ${record.status})`,
            sx: {
              cursor: canDownload ? 'pointer' : 'not-allowed',
              opacity: canDownload ? 1 : 0.5,
              // Removed pointerEvents restriction - let the button handle clicks
              '&:disabled': {
                cursor: 'not-allowed',
                opacity: 0.5
              }
            }
          }
        };
      }
    });

    return columns;
  };

  const getEnrichmentColumns = () => {
    const columns = [
      {
        field: "model_name",
        header: "Customer",
        align: "center",
        minWidth: "80px", // Reduced from 40px
        cellSx: { pr: 1 },
      },
      {
        field: "project_name",
        header: "Project",
        align: "center",
        minWidth: "80px", // Reduced from 40px
        render: (record) => ({
          type: "text",
          content: record.project || ""
        })
      },
      {
        field: "date_time",
        header: "Date",
        align: "center",
        minWidth: "80px", // Reduced from 120px
        render: (row) => {
          const date = new Date(row.date_time);
          return date.toLocaleDateString('en-CA'); // YYYY-MM-DD format
        }
      },
      {
        field: "processed_records",
        header: "Processed",
        align: "center",
        minWidth: "70px", // Reduced from 90px
        render: (record) => ({
          type: "text",
          content: record.processed_records?.toLocaleString() || "0"
        })
      },
      {
        field: "not_enriched",
        header: "Not Enriched",
        align: "center",
        minWidth: "80px", // Reduced from 90px
        render: (record) => ({
          type: "text",
          content: record.not_enriched?.toLocaleString() || "0"
        })
      },
      {
        field: "to_be_reviewed",
        header: "Reviewed",
        align: "center",
        minWidth: "90px", // Reduced from 90px (kept same as it's already compact)
        render: (record) => ({
          type: "text",
          content: record.to_be_reviewed?.toLocaleString() || "0"
        })
      },
      {
        field: "total_records",
        header: "Total",
        align: "center",
        minWidth: "20px", // Reduced from 90px
        render: (record) => ({
          type: "text",
          content: record.total_records?.toLocaleString() || "0"
        })
      },
      {
        field: "status",
        header: "Status",
        align: "center",
        minWidth: "580px", // Reduced from 130px
        render: (record) => getStatusCellConfig(record, true)
      }
    ];

    // Add created_by column if not MDM member
    if (role !== "MDM Members") {
      columns.push({
        field: "created_by",
        header: "Created By",
        align: "center",
        minWidth: "80px" // Reduced from 120px
      });
    }

    // Add actions column with enhanced support for processed status
    columns.push({
      field: "actions",
      header: "Actions",
      align: "center",
      minWidth: "50px", // Reduced from 120px
      render: (record) => {
        const isActionable = record.status?.toLowerCase() === "completed" ||
                            record.status?.toLowerCase() === "in progress" ||
                            record.status?.toLowerCase() === "processing" ||
                            record.status?.toLowerCase() === "processed"; // Added processed status

        return {
          type: "iconButton",
          config: {
            icon: <FileDownloadIcon style={{ color: isActionable ? "#002A5B" : "#cccccc" }} />,
            onClick: isActionable ? () => handleEnrichmentDownload(record.batch_id, record.tool_name, record.model_name, record.project) : undefined,
            disabled: !isActionable,
            tooltip: isActionable ? "Download" : `Cannot download (Status: ${record.status})`,
            sx: {
              cursor: isActionable ? 'pointer' : 'not-allowed',
              opacity: isActionable ? 1 : 0.5,
              '&:disabled': {
                cursor: 'not-allowed',
                opacity: 0.5
              }
            }
          }
        };
      }
    });

    return columns;
  };

  // Get columns based on the current state
  const columns = useMemo(() => {
    return isEnrichmentHistory ? getEnrichmentColumns() : getBaseColumns();
  }, [isEnrichmentHistory, role]);

  // Status options for the download dialog
  const statusOptions = [
    { id: 1, value: "All", label: "All" },
    { id: 2, value: "Processed", label: "Processed" },
    { id: 3, value: "Not Enriched", label: "Not Enriched" },
    { id: 4, value: "To be Reviewed", label: "To be Reviewed" }
  ];

  // File type options for Data Matching download modal
  const fileTypeOptions = [
    { id: 1, value: "all", label: "All Files" },
    { id: 2, value: "input", label: "Input File" },
    { id: 3, value: "output", label: "Output File" },
    { id: 4, value: "model", label: "Model File" }
  ];

  // Return all the necessary values and functions
  return {
    // State values
    pageTitle,
    searchTerm,
    page,
    rowsPerPage,
    filteredRecords,
    isLoading,
    isDownloading,
    role,
    showDetailView,
    selectedBatch,
    selectedCustomer,
    selectedProject,
    isEnrichmentHistory,
    downloadDialogOpen,
    dialogDownloadLoading,
    selectedStatus,
    statusOptions,
    columns,
    
    // New Data Matching modal states
    dataMatchingDownloadModalOpen,
    selectedFileType,
    fileTypeOptions,
    isDataMatchingTool,

    // Event handlers
    handleSearchChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleDownload,
    handleGoHome,
    handleCloseBackdrop,
    handleProcessClick,
    handleBackToHistory,
    handleEnrichmentProcessClick,
    handleEnrichmentDownload,
    handleDownloadDialogClose,
    handleConfirmDownload,
    setSelectedStatus,

    // New Data Matching modal handlers
    handleDataMatchingDownload,
    handleDataMatchingDownloadModalClose,
    setSelectedFileType,

    // Utility functions
    formatAccuracy,
    getAccuracyColor,
    getStatusCellConfig
  };
};

export default DataHistoryVM;
